import {
  LocationDto,
  PositionDto,
  ProductLine,
  RegionDto,
  TicketDto,
  TicketGeozoneDto,
} from '@trucktrax/trucktrax-ts-common';
import { Dispatch } from 'redux';

import { UPDATE_POSITION, UPDATE_INCOMING_POSITION } from '../constants/actionConstants';
import {
  INCOMING_MATERIALS_POSITION_DETAILS_PATH,
  MAPVIEWS_DETAILS_PATH,
  MAPVIEWS_TRACKED_OBJECT_ACTIVE_PATH,
} from '../constants/apiConstants';
import { INCOMING_MATERIALS_ROUTE } from '../constants/appConstants';
import { ERROR_TEXT_FETCH_MARKER_LIST } from '../constants/errorConstants';
import HTTP_CODES from '../constants/httpConstants';
import {
  PinLocationInfo,
  setPinMap,
  setPinRadius,
  setPositionList,
  setIncomingPositionList,
} from '../store/actions/mapviewActions';
import { getGeotraxBaseUrl } from '../util/apiUtil';
import { devErrorAndLog } from '../util/errorUtil';
import { getRequest } from './requestService';
import TrackingSocket from './sockets/trackingSocket';
import { PositionDetailsDto } from '../types';
import { shouldDisplayPosition } from '../util/positionsUtil';
import { ReduxState } from '../store';

type GetState = () => {
  currentRegion: RegionDto;
  selectedProductLine: ProductLine;
};

export function getMapview(regionUrl?: string, force: boolean = false) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    // The main mapview fetch scopes to region + primary product line, which
    // is the wrong shape for /geotrax/incomingMaterials. The IM page loads
    // its own positions via getIncomingMaterialsPositions. Callers that need
    // to repopulate positionList on the way *out* of IM pass force=true.
    if (
      !force &&
      typeof window !== 'undefined' &&
      window.location.pathname.includes(INCOMING_MATERIALS_ROUTE)
    ) {
      return;
    }
    const url = `${getGeotraxBaseUrl()}${MAPVIEWS_DETAILS_PATH}`;
    const { selectedProductLine: primaryProductLine, currentRegion } = getState();
    const region = regionUrl || currentRegion?.url;

    if (!region) {
      return;
    }

    // Due to selectedProductLine not being in the same
    // state slice as the selected/current region we have to ignore invalid
    // intermediate out of sync product line and region combinations
    const productLineMatches = currentRegion?.productLines?.includes(primaryProductLine);
    if (!productLineMatches) {
      return;
    }

    const params = {
      region,
      primaryProductLine,
      isActive: true,
    };
    try {
      const response = await getRequest(url, params);
      const allPositions = response.data.items as PositionDetailsDto[];
      const displayablePositions = allPositions.filter(shouldDisplayPosition);
      dispatch(setPositionList(displayablePositions));
    } catch (e: any) {
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      dispatch(
        devErrorAndLog(
          e.toString(),
          ERROR_TEXT_FETCH_MARKER_LIST,
          e,
          `- regionUrl:${regionUrl}`,
          consoleOnly,
        ) as any,
      );
    }
  };
}

/**
 * Loads truck positions for the Incoming Materials map. Filters server-side by
 * the user's assigned destination plants — no region/productLine filter so
 * cross-region trucks delivering to those plants are included.
 */
export function getIncomingMaterialsPositions(destinationPlants: string[]) {
  return async (dispatch: Dispatch<any>) => {
    if (!destinationPlants.length) return;
    const url = `${getGeotraxBaseUrl()}${INCOMING_MATERIALS_POSITION_DETAILS_PATH}`;
    const params = {
      destinationPlants,
      isActive: true,
    };
    try {
      const response = await getRequest(url, params);
      const positions = (response.data.items ?? []) as PositionDetailsDto[];
      dispatch(setIncomingPositionList(positions));
    } catch (e: any) {
      const consoleOnly = e.response?.status === HTTP_CODES.forbidden;
      dispatch(
        devErrorAndLog(
          e.toString(),
          ERROR_TEXT_FETCH_MARKER_LIST,
          e,
          `- destinationPlants:${destinationPlants.join(',')}`,
          consoleOnly,
        ) as any,
      );
    }
  };
}

export function getDirections(
  points?: (LocationDto | undefined)[],
  callback?: (
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => void,
) {
  if (!points || !points.length || !callback) {
    return;
  }

  const origin = new google.maps.LatLng(points[0]?.latitude ?? 0, points[0]?.longitude ?? 0);
  const destination = new google.maps.LatLng(
    points[points.length - 1]?.latitude ?? 0,
    points[points.length - 1]?.longitude ?? 0,
  );
  // Only include middle points as waypoints (exclude origin and destination)
  const inBetween = points.slice(1, -1).map(w => ({
    location: new google.maps.LatLng(w?.latitude ?? 0, w?.longitude ?? 0),
    stopover: true,
  }));

  const service = new google.maps.DirectionsService();
  service.route(
    {
      origin,
      destination,
      waypoints: inBetween,
      travelMode: google.maps.TravelMode.DRIVING,
    },
    callback,
  );
}

export function getDistanceMatrix(
  truckPoint: LocationDto,
  destinationPoint: LocationDto | string,
  callback: (response: google.maps.DistanceMatrixResponse | null) => void,
) {
  const truck = new google.maps.LatLng(
    truckPoint.latitude ?? truckPoint.lat ?? 0,
    truckPoint.longitude ?? truckPoint.lng ?? 0,
  );

  // Handle both coordinate objects and address strings
  const destination =
    typeof destinationPoint === 'string'
      ? destinationPoint
      : new google.maps.LatLng(
          destinationPoint.latitude ?? destinationPoint.lat ?? 0,
          destinationPoint.longitude ?? destinationPoint.lng ?? 0,
        );

  const service = new google.maps.DistanceMatrixService();

  service.getDistanceMatrix(
    {
      origins: [truck],
      destinations: [destination],
      travelMode: google.maps.TravelMode.DRIVING,
      drivingOptions: {
        departureTime: new Date(),
        trafficModel: google.maps.TrafficModel.PESSIMISTIC,
      },
      unitSystem: google.maps.UnitSystem.IMPERIAL,
    },
    callback,
  );
}

export interface PositionWithTicketGeozone extends PositionDto {
  ticket: {
    geoZone: TicketGeozoneDto;
    url: string;
  };
  ticketStatus?: PositionDto;
  plantLocation?: LocationDto;
}

export function updatePositionWithTruckEta(position: PositionWithTicketGeozone) {
  return (dispatch: Dispatch, getState: GetState) => {
    const { ticketsList, incomingTicketsList, positionList, incomingPositionList } =
      getState() as ReduxState;
    let { ticket = position.ticket ?? position.ticketStatus?.ticket ?? {} }: TicketDto = position;

    if (!ticket.geoZone && ticket.url) {
      // IM tickets live in incomingTicketsList; fall back to it so ETA works on the IM map.
      ticket =
        ticketsList?.find(source => source.url === ticket.url) ??
        incomingTicketsList?.find(source => source.url === ticket.url) ??
        ticket;
    }

    const {
      location = position.ticketStatus?.location,
      status: ticketStatus = position.ticketStatus?.status || '',
    } = position;
    const hasLocation = Object.keys(location || {}).length > 0;
    const truckLatLng = hasLocation ? location : null;

    const hasTicketGeoZone = ticket?.geoZone?.zone?.circle?.center;

    // Determine destination for ETA calculation
    // Priority: geoZone coordinates > address string (for forward trips only)
    let destinationLatLng: LocationDto | string | null = hasTicketGeoZone
      ? ticket!.geoZone!.zone!.circle!.center!
      : null;

    // Fall back to address string if geoZone coordinates are not available
    // Google Maps API accepts both coordinate objects and address strings
    if (!destinationLatLng && ticket?.address) {
      destinationLatLng = ticket.address;
    }

    // For Returning status, always use plantLocation (overrides ticket destination)
    if (ticketStatus === 'Returning' && position?.plantLocation) {
      destinationLatLng = position?.plantLocation;
    }

    if (!truckLatLng || !destinationLatLng) {
      return;
    }

    getDistanceMatrix(
      truckLatLng,
      destinationLatLng,
      (resp: google.maps.DistanceMatrixResponse | null) => {
        const responseHasNoData = !resp || !Array.isArray(resp.rows) || resp.rows.length === 0;
        if (responseHasNoData) {
          return;
        }

        const [firstRow] = resp.rows;
        const firstRowHasNoData =
          !firstRow || !Array.isArray(firstRow.elements) || firstRow.elements.length === 0;
        if (firstRowHasNoData) {
          return;
        }

        const firstElement = firstRow.elements.at(0);
        if (!firstElement) {
          return;
        }

        const { status, ...rest } = firstElement;
        const payload = { ...position, ...rest };
        // Target the slice(s) that already hold this position so the ETA
        // refresh doesn't create a phantom entry in the other list. A truck
        // present in both slices (in-region truck that's also an IM ticket)
        // receives both updates. If the truck is in neither slice — e.g.
        // selectTruckMarker landed on its determineMarkerPosition(marker)
        // fallback — we skip the dispatch entirely rather than appending a
        // ghost entry to positionList.
        const inStandard = positionList?.some(p => p.id === position.id);
        const inIncoming = incomingPositionList?.some(p => p.id === position.id);
        if (inStandard) {
          dispatch({ type: UPDATE_POSITION, payload });
        }
        if (inIncoming) {
          dispatch({ type: UPDATE_INCOMING_POSITION, payload });
        }
      },
    );
  };
}

export async function isTrackedObjectActive(trackedObjectUrl: string) {
  const url = `${getGeotraxBaseUrl()}${MAPVIEWS_TRACKED_OBJECT_ACTIVE_PATH}`;
  const params = {
    trackedObjectUrl,
  };

  try {
    const response = await getRequest(url, params);
    return response.data;
  } catch (e) {
    return false;
  }
}

export async function trackingSocketDisconnect(): Promise<void> {
  await TrackingSocket.disconnect(true);
}

export async function trackingSocketInit(currentRegion: string): Promise<void> {
  await trackingSocketDisconnect();
  await TrackingSocket.connect(currentRegion);
}

export async function changeTrackingSocketConnection(region: string): Promise<void> {
  await TrackingSocket.attemptConnectionChange(region);
}

export function activatePinMap(pinInfo: PinLocationInfo) {
  return (dispatch: Dispatch) => {
    dispatch(setPinMap(pinInfo));
  };
}

export function updatePinRadius(radius: string) {
  return (dispatch: Dispatch) => {
    dispatch(setPinRadius(radius));
  };
}
